import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fbLimit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Firestore,
  type Timestamp,
} from 'firebase/firestore';
import type { FuelType } from '@kawai/shared';
import { db as defaultDb } from '../firebase.js';

/**
 * F-02: 燃料油価格 read 専用リポジトリ。
 *
 * - 書込は F-03 で実装する Callable 経由のみ
 *   (`firestore.rules` の `fuel_prices` / `fuel_price_list` は write deny 済)
 * - doc id は tenantId（kawai-takefu21ss 単一スタートだが将来も同じ採用）
 *
 * 履歴 (`fuel_price_history`) は collection で `tenantId` フィールド +
 * `createdAt` 降順 + limit=10 を取得する。Firestore index (tenantId asc, createdAt desc)
 * は F-03 で `firestore.indexes.json` に追加する想定。
 */

/** 燃料油の基準価格 doc。`updatedAt` / `updatedBy` は F-03 Callable 側で書き込む。 */
export interface FuelPricesDoc {
  haioku: number;
  regular: number;
  keiyu: number;
  touyu: number;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

/** 一覧表の 1 行（4 燃料）。 */
export type FuelPriceRow = Record<FuelType, number>;

/**
 * 一覧表 doc。
 *
 * F-16 で `rows: { [categoryId]: 4 燃料 }` の動的 shape に移行（区分はテナント設定）。
 * 旧 shape（F-03 / aisin・kawai・kakeuri の top-level 直置き）の既存 doc も読めるよう
 * 両フィールドを optional にし、読み手は `getFuelPriceListRows`（@kawai/shared）で
 * shape 差を吸収する。
 */
export interface FuelPriceListDoc {
  rows?: Record<string, FuelPriceRow>;
  aisin?: FuelPriceRow;
  kawai?: FuelPriceRow;
  kakeuri?: FuelPriceRow;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

/**
 * F-16: テナント別の燃料設定 doc（一覧表の区分 / 派生価格の値引き幅）。
 * 生 doc は欠落・型不正があり得るため、読み手は `normalizeFuelSettings`
 * （@kawai/shared）で `FuelSettings` に正規化してから使う。
 */
export interface FuelSettingsDoc {
  categories?: unknown;
  pricing?: unknown;
  updatedAt?: Timestamp | string;
  updatedBy?: string;
}

/** 履歴エントリ。F-03 で書込される shape を read 側でも参照する。 */
export interface FuelPriceHistoryEntry {
  id: string;
  tenantId: string;
  /** 'base' (基準) | 'list' (一覧) | 'bulk' (一括上げ下げ) などの種別 */
  kind: string;
  /** 1 行サマリ（UI 表示用）。詳細 diff は `before` / `after` に格納される想定 */
  summary?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  createdAt: Timestamp;
  createdBy: string;
}

/** `fuel_prices/{tenantId}` を取得。doc が無ければ null。 */
export async function fetchFuelPrices(
  tenantId: string,
  db: Firestore = defaultDb,
): Promise<FuelPricesDoc | null> {
  const snap = await getDoc(doc(db, 'fuel_prices', tenantId));
  if (!snap.exists()) return null;
  return snap.data() as FuelPricesDoc;
}

/** `fuel_price_list/{tenantId}` を取得。doc が無ければ null。 */
export async function fetchFuelPriceList(
  tenantId: string,
  db: Firestore = defaultDb,
): Promise<FuelPriceListDoc | null> {
  const snap = await getDoc(doc(db, 'fuel_price_list', tenantId));
  if (!snap.exists()) return null;
  return snap.data() as FuelPriceListDoc;
}

/** F-16: `fuel_settings/{tenantId}` を取得。doc が無ければ null（= デフォルト設定で動作）。 */
export async function fetchFuelSettings(
  tenantId: string,
  db: Firestore = defaultDb,
): Promise<FuelSettingsDoc | null> {
  const snap = await getDoc(doc(db, 'fuel_settings', tenantId));
  if (!snap.exists()) return null;
  return snap.data() as FuelSettingsDoc;
}

/**
 * F-11: 印刷ページ向けの onSnapshot 購読。
 *
 * DwPricePage / PriceListPage は admin がリアルタイムで価格を更新したらすぐ
 * 反映したいので getDoc では無く onSnapshot で購読する。
 * 戻り値は購読解除関数（useEffect cleanup で呼ぶ想定）。
 */
export function subscribeFuelPrices(
  tenantId: string,
  callback: (data: FuelPricesDoc | null) => void,
  db: Firestore = defaultDb,
): () => void {
  const ref = doc(db, 'fuel_prices', tenantId);
  return onSnapshot(
    ref,
    (snap) => callback(snap.exists() ? (snap.data() as FuelPricesDoc) : null),
    (err) => {
      // eslint-disable-next-line no-console -- 購読エラーは握り潰さずログに残す
      console.error('[fuelPrices] subscribeFuelPrices error:', err);
      callback(null);
    },
  );
}

export function subscribeFuelPriceList(
  tenantId: string,
  callback: (data: FuelPriceListDoc | null) => void,
  db: Firestore = defaultDb,
): () => void {
  const ref = doc(db, 'fuel_price_list', tenantId);
  return onSnapshot(
    ref,
    (snap) => callback(snap.exists() ? (snap.data() as FuelPriceListDoc) : null),
    (err) => {
      // eslint-disable-next-line no-console -- 購読エラーは握り潰さずログに残す
      console.error('[fuelPrices] subscribeFuelPriceList error:', err);
      callback(null);
    },
  );
}

/**
 * F-16: 印刷ページ向けの fuel_settings 購読（区分・値引き幅の変更を即反映）。
 *
 * 「doc 無し（正常系 = デフォルト設定で動作）」と「読取失敗」を呼出側が区別できるよう、
 * エラー時は第 2 引数 error を渡す（レビュー #5/#10: 失敗を無警告でデフォルトに
 * フォールバックさせると誤った値引き幅の印刷物が出るため）。
 */
export function subscribeFuelSettings(
  tenantId: string,
  callback: (data: FuelSettingsDoc | null, error?: Error) => void,
  db: Firestore = defaultDb,
): () => void {
  const ref = doc(db, 'fuel_settings', tenantId);
  return onSnapshot(
    ref,
    (snap) => callback(snap.exists() ? (snap.data() as FuelSettingsDoc) : null),
    (err) => {
      // eslint-disable-next-line no-console -- 購読エラーは握り潰さずログに残す
      console.error('[fuelPrices] subscribeFuelSettings error:', err);
      callback(null, err);
    },
  );
}

/**
 * `fuel_price_history` を `tenantId == :tenantId` で絞り、
 * `createdAt desc` で直近 N 件取得する（デフォ 10 件）。
 *
 * Firestore index がまだ無い段階で呼ばれた場合は SDK が console に index 提案 URL を出すので、
 * F-03 の deploy で `firestore.indexes.json` に登録する。
 */
export async function fetchFuelPriceHistory(
  tenantId: string,
  limit: number = 10,
  db: Firestore = defaultDb,
): Promise<FuelPriceHistoryEntry[]> {
  const q = query(
    collection(db, 'fuel_price_history'),
    where('tenantId', '==', tenantId),
    orderBy('createdAt', 'desc'),
    fbLimit(limit),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<FuelPriceHistoryEntry, 'id'>),
  }));
}
